import { handleActions, combineActions } from 'redux-actions'
import { combineReducers } from 'redux'
import * as actions from './actions'
import { defaultArticleData, defaultCategoryData, defaultSectionData } from './model'
import { reorderArrayItems } from './helpers'
import uniqBy from 'lodash/uniqBy'


const uniqByMessage = e => (e.message)

const article = handleActions({
  [actions.createArticleSuccess]: (state, action) => ({...state, ...action.payload, isSaving: false}),
  [actions.createArticlePending]: (state, action) => ({...state, isSaving: true, errors: []}),
  [actions.editArticlePending]: (state, action) => ({...state, isSaving: true, isSaved: false}),
  [actions.fetchArticleSuccess]: (state, action) => ({ ...state, ...action.payload, backEndCopyOfArticleStatus: action.payload.status }),
  [actions.editArticleSuccess]: (state, action) => ({
    ...state,
    version: action.payload.version,
    isSaving: false,
    isSaved: true,
    backEndCopyOfArticleStatus: action.payload.status
  }),
  [actions.editArticleFailed]: (state, action) => ({...state, isSaving: false, isSaved: false}),
  [actions.createArticleFailed]: (state, action) => ({...state, errors: [...state.errors, action.payload], isSaving: false}),
  [actions.setArticle]: (state, action) => ({ ...state, ...action.payload }),
  [actions.clearArticle]: () => defaultArticleData,
  [actions.setDocument]: (state, action) => Object.assign({}, state, {document: action.payload}),
  [actions.setTitle]: (state, action) => ({...state, title: action.payload, errors: state.errors.filter(e => !e.title)}),
  [actions.checkTitleSuccess]: (state, action) => ({...state, errors: state.errors.filter(e => !e.title)}),
  [actions.checkTitleFailed]: (state, action) => ({...state, errors: uniqBy([...state.errors, action.payload], uniqByMessage) }),
  [actions.checkSlugSuccess]: (state, action) => ({...state, errors: state.errors.filter(e => !e.slug)}),
  [actions.checkSlugFailed]: (state, action) => ({...state, errors: uniqBy([...state.errors, action.payload], uniqByMessage) }),
  [actions.setSlug]: (state, action) => Object.assign({}, state, {slug: action.payload}, {errors: state.errors.filter(e => !e.slug)}),
  [actions.setStatus]: (state, action) => Object.assign({}, state, {status: action.payload}),
  [actions.setArticleCategory]: (state, action) => ({...state, categoryId: action.payload, sectionId: null}),
  [actions.setArticleSection]: (state, action) => ({...state, sectionId: action.payload.sectionId, categoryId: action.payload.categoryId}),
  [actions.deleteArticle]: (state, action) => ({...state, isBeingDeletedFromEditor: action.payload.isFromEditor}),
  [actions.clearArticleErrors]: (state, action) => ({...state, errors: []})
}, defaultArticleData)

const category = handleActions({
  [actions.changeCategoryName]: (state, action) => ({...state, name: action.payload}),
  [actions.changeCategoryDescription]: (state, action) => ({...state, description: action.payload}),
  [actions.setCategory]: (state, action) => action.payload,
  [actions.clearCategory]: () => defaultCategoryData
}, defaultCategoryData)

const articleChanged = handleActions({
  [combineActions(
    actions.setTitle,
    actions.setSlug,
    actions.setStatus,
    actions.setDocument,
    actions.setArticleCategory,
    actions.setArticleSection
  )]: () => true,
  [actions.editArticleSuccess]: () => false
}, false)

const fetchArticleStatus = handleActions({
  [combineActions(
    actions.fetchArticle,
    actions.fetchArticleSuccess,
    actions.fetchArticlePending,
    actions.fetchArticleFailed
  )]: (state, action) => action.type
}, false)

const fetchCategoriesStatus = handleActions({
  [combineActions(
    actions.fetchCategories,
    actions.fetchCategoriesSuccess,
    actions.fetchCategoriesPending,
    actions.fetchCategoriesFailed
  )]: (state, action) => action.type
}, false)

const categories = handleActions({
  [actions.fetchCategoriesSuccess]: (state, action) => action.payload,
  [actions.saveSectionEdits]: (state, action) => {
    const categories = [...state]
    const categoryIndex = categories.findIndex((category) => category.sections.some(section => section.id === action.payload.id))
    const sectionIndex = categories[categoryIndex].sections.findIndex(section => section.id === action.payload.id)
    categories[categoryIndex].sections[sectionIndex] = action.payload
    
    return categories
  },
  
  [actions.createSectionSuccess]: (state, action) => {
    const categories = [...state]
    const categoryIndex = categories.findIndex((category) => category.id === action.payload.categoryId)
    categories[categoryIndex].sections = [...categories[categoryIndex].sections, action.payload]
  
    return categories
  },
  
  [actions.deleteSectionSuccess]: (state, action) => {
    const categories = [...state]
    const categoryIndex = categories.findIndex(category => category.sections.some(section => section.id === action.payload.id))
    const sectionIndex = categories[categoryIndex].sections.findIndex(section => section.id === action.payload.id)
    
    categories[categoryIndex].sections.splice(sectionIndex, 1)
    
    return categories
  },
  [actions.deleteArticle]: (state, action) => {
    const { article } = action.payload
    return [...state].map(category => {
      const documents = category.documents.filter(document => document.id !== article.id)
      const sections = category.sections.map(section => {
        const documents = section.documents.filter(document => document.id !== article.id)
        return { ...section, documents }
      })
      return { ...category, documents, sections }
    })
  },
  [actions.deleteCategory]: (state, action) => [...state].filter(category => category.id !== action.payload.id),
  [actions.createCategorySuccess]: (state, action) => [...state, action.payload],
  [actions.editCategory]: (state, action) => {
    const categories = [...state]
    return categories.map(category => {
      
      if (category.id === action.payload.id) {
        return ({ ...category, ...action.payload})
      }
      
      return category
    })
  },
  [actions.reorderCategories]: (state, action) => reorderArrayItems(action.payload, [...state]),
  [actions.reorderCategoryDocuments]: (state, action) => {
    const categories = [...state]
    const categoryIndex = categories.findIndex((category) => category.id === action.payload.categoryId)
    categories[categoryIndex].documents = reorderArrayItems(action.payload, categories[categoryIndex].documents)
    return categories
  },
  [actions.reorderCategorySections]: (state, action) => {
    const categories = [...state]
    const categoryIndex = categories.findIndex((category) => category.id === action.payload.categoryId)
    categories[categoryIndex].sections = reorderArrayItems(action.payload, categories[categoryIndex].sections)
    return categories
  },
  [actions.reorderSectionDocuments]: (state, action) => {
    const categories = [...state]
    const categoryIndex = categories.findIndex((category) => category.id === action.payload.categoryId)
    const section = categories[categoryIndex].sections.find(section => section.id === action.payload.sectionId)
    
    section.documents = reorderArrayItems(action.payload, section.documents)
    
    return categories
  },
  [actions.reorderCategoriesSuccess]: (state, action) => action.payload
}, [])

const sectionToEdit = handleActions({
  [actions.createSectionSuccess]: () => defaultSectionData,
  [actions.setSectionToEdit]: (state, action) => action.payload,
  [actions.changeSectionName]: (state, action) => ({...state, name: action.payload}),
  [actions.changeSectionDescription]: (state, action) => ({...state, description: action.payload}),
  [actions.changeSectionCategory]: (state, action) => ({...state, categoryId: action.payload}),
  [actions.clearSectionToEdit]: () => defaultSectionData,
  [actions.saveSectionEditsSuccess]: () => defaultSectionData
}, defaultSectionData)

const sectionToDelete = handleActions({
  [actions.setSectionToDelete]: (state, action) => action.payload,
  [actions.clearSectionToDelete]: () => defaultSectionData,
  [actions.deleteSectionSuccess]: () => defaultSectionData
}, defaultSectionData)

export default combineReducers({
  article,
  category,
  categories,
  articleChanged,
  fetchArticleStatus,
  fetchCategoriesStatus,
  sectionToEdit,
  sectionToDelete
})
